/**
 * TransferDecisionStep — decision review + confirm dialog for transfer verification.
 * Extracted from TransferVerification.tsx for readability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { TransferItem } from '../../../services/inventoryService';
import type { ScannedBox, DecisionMode } from './TransferScanningStep';
import {
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  X,
  Pause,
} from 'lucide-react';

interface ItemDecision {
  item: TransferItem;
  acceptedCount: number;
  acceptedWeight: number;
  rejectedCount: number;
  rejectedWeight: number;
  onHoldCount?: number;
  onHoldWeight?: number;
  action?: 'approve' | 'hold' | 'reject';
  onHoldReason?: string;
  notes?: string;
  editing?: boolean;
}

interface TransferDecisionStepProps {
  selectedTransfer: { transfer_number: string } | null;
  setStep: (step: 'badge' | 'transfer' | 'scanning' | 'decision') => void;
  scannedBoxes: ScannedBox[];
  decisionMode: DecisionMode;
  itemDecisions: ItemDecision[];
  submitting: boolean;
  showConfirmDialog: boolean;
  setShowConfirmDialog: (v: boolean) => void;
  handleSubmit: () => void;
}

const TransferDecisionStep: React.FC<TransferDecisionStepProps> = ({
  selectedTransfer,
  setStep,
  scannedBoxes,
  decisionMode,
  itemDecisions,
  submitting,
  showConfirmDialog,
  setShowConfirmDialog,
  handleSubmit,
}) => {
  const { t } = useLanguage();

  // Calculate totals for whole transfer mode
  const transferTotals = {
    expectedQty: itemDecisions.reduce((sum, d) => sum + d.item.expected_quantity, 0),
    expectedWeight: itemDecisions.reduce((sum, d) => sum + d.item.expected_weight, 0),
    acceptedQty: itemDecisions.reduce((sum, d) => sum + d.acceptedCount, 0),
    acceptedWeight: itemDecisions.reduce((sum, d) => sum + d.acceptedWeight, 0),
    rejectedQty: itemDecisions.reduce((sum, d) => sum + d.rejectedCount, 0),
    rejectedWeight: itemDecisions.reduce((sum, d) => sum + d.rejectedWeight, 0),
  };

  const allDecisionsComplete = scannedBoxes.length > 0;
  const wholeTransferComplete = scannedBoxes.length > 0;

  return (
    <>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setStep('scanning')}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('transferVerification.perItemDecision')}
          </h2>
        </div>

        {/* Whole Transfer Mode - Simple summary, auto-approve all scanned */}
        {decisionMode === 'wholeTransfer' && (
          <div className="space-y-4">
            {/* Transfer Summary Card */}
            <div className="p-4 rounded-lg border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-lg">
                    {t('transferVerification.transfer')} #{selectedTransfer?.transfer_number}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {t('transferVerification.readyToSubmit')}
                  </p>
                </div>
              </div>

              {/* Totals - just Expected vs Scanned */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transferVerification.expected')}:</span>
                  <p className="font-mono text-xl text-gray-900 dark:text-white">
                    {transferTotals.expectedQty} {t('transferVerification.boxes')}
                  </p>
                  <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                    {transferTotals.expectedWeight.toFixed(2)} lbs
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="text-green-600 dark:text-green-400 text-xs">{t('transferVerification.scanned')}:</span>
                  <p className="font-mono text-xl text-green-600 dark:text-green-400">
                    {transferTotals.acceptedQty} {t('transferVerification.boxes')}
                  </p>
                  <p className="font-mono text-sm text-green-600 dark:text-green-400">
                    {transferTotals.acceptedWeight.toFixed(2)} lbs
                  </p>
                </div>
              </div>
            </div>

            {/* Items breakdown */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('transferVerification.itemsBreakdown')}:
              </p>
              <div className="space-y-1 text-sm">
                {itemDecisions.map((decision) => (
                  <div key={decision.item.item_number} className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>{decision.item.item_name || decision.item.item_number}</span>
                    <span className="font-mono">
                      <span className="text-gray-500">{decision.item.expected_quantity} →</span>
                      <span className="text-green-600 ml-2">{decision.acceptedCount}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Per Item Mode - Summary of accepted/rejected per item */}
        {decisionMode === 'perItem' && (
          <div className="space-y-4">
            {itemDecisions.map((decision) => (
              <div
                key={decision.item.item_number}
                className={`p-4 rounded-lg border ${
                    decision.acceptedCount === 0 && (decision.onHoldCount || 0) === 0 && decision.rejectedCount === 0
                    ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                    : decision.rejectedCount > 0 && decision.acceptedCount === 0
                      ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                      : decision.rejectedCount > 0
                        ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                        : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                  }`}
              >
                {/* Item header */}
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {decision.item.item_name || decision.item.item_number}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {decision.item.item_number}
                    </p>
                  </div>
                  {decision.acceptedCount === 0 && (decision.onHoldCount || 0) === 0 && decision.rejectedCount === 0 ? (
                    <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                  ) : decision.rejectedCount > 0 && decision.acceptedCount === 0 ? (
                    <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                  ) : decision.rejectedCount > 0 ? (
                    <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  )}
                </div>

                {/* Expected vs Accepted/On Hold/Rejected - 2x2 on mobile */}
                {(() => {
                  const isPacking = decision.item.is_packing_material === 'Y';
                  const subQty = decision.item.item_sub_quantity || 0;
                  const unitType = decision.item.item_unit || 'pk';
                  const formatVal = (qty: number, weight: number) =>
                    isPacking && subQty > 0
                      ? `${qty} ${unitType} / ${qty * subQty} pcs`
                      : isPacking
                        ? `${qty} ${unitType}`
                        : `${qty} / ${weight.toFixed(2)} lbs`;
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                        <span className="text-gray-500 dark:text-gray-400 text-xs block">{t('transferVerification.expected')}:</span>
                        <p className="font-mono text-gray-900 dark:text-white">
                          {formatVal(decision.item.expected_quantity, decision.item.expected_weight)}
                        </p>
                      </div>
                      <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                        <span className="text-green-600 dark:text-green-400 text-xs block">{t('transferVerification.accepted')}:</span>
                        <p className="font-mono text-green-600 dark:text-green-400">
                          {formatVal(decision.acceptedCount, decision.acceptedWeight)}
                        </p>
                      </div>
                      <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                        <span className="text-amber-600 dark:text-amber-400 text-xs block">{t('transferVerification.onHold')}:</span>
                        <p className="font-mono text-amber-600 dark:text-amber-400">
                          {formatVal(decision.onHoldCount || 0, decision.onHoldWeight || 0)}
                        </p>
                      </div>
                      <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                        <span className="text-red-600 dark:text-red-400 text-xs block">{t('transferVerification.rejected')}:</span>
                        <p className="font-mono text-red-600 dark:text-red-400">
                          {formatVal(decision.rejectedCount, decision.rejectedWeight)}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Per-box breakdown: all boxes for this item */}
                {(() => {
                  const itemBoxes = scannedBoxes
                    .map((b, i) => ({ ...b, boxNumber: i + 1 }))
                    .filter(b => b.item_number === decision.item.item_number && !b.notInTransfer);
                  if (itemBoxes.length === 0) return null;
                  return (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 space-y-1">
                      {itemBoxes.map((box) => (
                        <div key={box.boxNumber} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1.5">
                            {box.status === 'accepted' ? (
                              <Check className="w-3 h-3 flex-shrink-0 text-green-600 dark:text-green-400" />
                            ) : box.status === 'on_hold' ? (
                              <Pause className="w-3 h-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <X className="w-3 h-3 flex-shrink-0 text-red-600 dark:text-red-400" />
                            )}
                            <span className="text-gray-500 dark:text-gray-400 font-mono">#{box.boxNumber}</span>
                            <span className={
                              box.status === 'accepted' ? 'text-green-600 dark:text-green-400'
                                : box.status === 'on_hold' ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-red-600 dark:text-red-400'
                            }>
                              {box.status === 'on_hold' ? `${t('transferVerification.onHold')}: ${box.onHoldReason ? t(`transferVerification.onHoldReasons.${box.onHoldReason}`) : ''}` : ''}
                              {box.status !== 'on_hold' && box.rejectReason ? box.rejectReason : ''}
                              {box.status !== 'on_hold' && !box.rejectReason && !box.manualReason ? (box.status === 'accepted' ? t('transferVerification.accepted') : t('transferVerification.rejected')) : ''}
                            </span>
                            {box.manualReason && (
                              <span className="text-xs px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded flex-shrink-0">
                                {t('transferVerification.manual')}: {box.manualReason}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                            {decision.item.is_packing_material === 'Y' && (decision.item.item_sub_quantity || 0) > 0
                              ? `${box.quantity || 1} ${decision.item.item_unit || 'pk'} / ${(box.quantity || 1) * (decision.item.item_sub_quantity || 0)} pcs`
                              : `${(box.weight ?? 0).toFixed(2)} lbs`}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Items not in transfer */}
        {scannedBoxes.filter(b => b.notInTransfer).length > 0 && (
          <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                {t('transferVerification.itemsNotInTransfer')}
              </h3>
            </div>
            <div className="space-y-1 text-sm">
              {scannedBoxes.map((b, i) => ({ ...b, boxNumber: i + 1 })).filter(b => b.notInTransfer).map((box) => (
                <div key={box.boxNumber}>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-300">
                      {box.status === 'accepted' ? (
                        <Check className="w-4 h-4 text-green-600 inline mr-1" />
                      ) : (
                        <X className="w-4 h-4 text-red-600 inline mr-1" />
                      )}
                      <span className="text-gray-500 dark:text-gray-400 font-mono text-xs mr-1">#{box.boxNumber}</span>
                      {box.item_name || box.item_number}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">{(box.weight ?? 0).toFixed(2)} lbs</span>
                  </div>
                  {(box.rejectReason || box.manualReason) && (
                    <div className="ml-5 mt-0.5 space-y-0.5">
                      {box.rejectReason && (
                        <p className="text-xs text-red-600 dark:text-red-400">{box.rejectReason}</p>
                      )}
                      {box.manualReason && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">{t('transferVerification.manual')}: {box.manualReason}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={decisionMode === 'perItem' ? (!allDecisionsComplete || submitting) : (!wholeTransferComplete || submitting)}
          className="w-full mt-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg font-medium"
        >
          <Check className="w-5 h-5" />
          {t('transferVerification.submitAll')}
        </button>
      </div>

      {/* Confirm dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('transferVerification.confirmSubmit')}
              </h3>
            </div>

            <div className="flex gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <div className="flex-1 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {decisionMode === 'wholeTransfer'
                    ? scannedBoxes.filter(b => !b.notInTransfer).length
                    : scannedBoxes.filter(b => b.status === 'accepted').length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('transferVerification.accepted')}</p>
              </div>
              <div className="flex-1 text-center border-x border-gray-200 dark:border-gray-700">
                <p className="text-2xl font-bold text-amber-600">
                  {decisionMode === 'wholeTransfer'
                    ? 0
                    : scannedBoxes.filter(b => b.status === 'on_hold').length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('transferVerification.hold')}</p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {decisionMode === 'wholeTransfer'
                    ? 0
                    : scannedBoxes.filter(b => b.status === 'rejected').length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('transferVerification.rejected')}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold"
              >
                {submitting ? t('common.processing') : t('transferVerification.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransferDecisionStep;
